import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export type CalendarEvent = {
  id: string;
  title: string;
  start: string;
  end: string;
  html_link: string;
  attendees: string[];
  location: string | null;
  description: string | null;
  all_day: boolean;
};

export function useCalendarEvents(days = 7) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["calendar-events", user?.id, days],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase.functions.invoke("gcal-list-events", {
        body: { user_id: user.id, days },
      });
      if (error) throw error;
      return (data?.events || []) as CalendarEvent[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCalendarEventsRange(timeMin: string, timeMax: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["calendar-events-range", user?.id, timeMin, timeMax],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase.functions.invoke("gcal-list-events", {
        body: { user_id: user.id, time_min: timeMin, time_max: timeMax },
      });
      if (error) throw error;
      return (data?.events || []) as CalendarEvent[];
    },
    enabled: !!user && !!timeMin && !!timeMax,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateCalendarEvent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      start_time: string;
      end_time?: string;
      description?: string;
      attendees?: string[];
    }) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("gcal-create-event", {
        body: { user_id: user.id, ...input },
      });
      if (error) throw error;
      return data as { success: boolean; event_id: string; html_link: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-events-range"] });
    },
  });
}

export function useUpdateCalendarEvent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      event_id: string;
      title?: string;
      start_time?: string;
      end_time?: string;
      description?: string;
      attendees?: string[];
    }) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("gcal-update-event", {
        body: { user_id: user.id, ...input },
      });
      if (error) throw error;
      return data as { success: boolean; event_id: string; html_link: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-events-range"] });
    },
  });
}

export function useDeleteCalendarEvent() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (event_id: string) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase.functions.invoke("gcal-delete-event", {
        body: { user_id: user.id, event_id },
      });
      if (error) throw error;
      return data as { success: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
      queryClient.invalidateQueries({ queryKey: ["calendar-events-range"] });
    },
  });
}
